/**
 * Episode Try Page
 *
 * Web-based episode testing interface with WebRTC conversation.
 * Allows admins to test episodes with language selection and live transcript.
 *
 * Story 10.10: Episode Try Page with WebRTC Integration
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PipecatClient } from '@pipecat-ai/client-js';
import { PipecatClientProvider, usePipecatClient } from '@pipecat-ai/client-react';
import { WebLiveKitTransport } from '../transport/WebLiveKitTransport';
import { useEpisode } from '../services/episodesApi';
import {
  getSessionMetadata,
  connectSession,
  finishSession,
  type GoalProgressEntry,
} from '../services/episodeSessionApi';
import { useInitialData } from '../../../contexts/InitialDataContext';
import logger from 'utils/logger';
// TODO: Re-enable when RTVI message handling is implemented
// import { isGoalCompletedMessage, type GoalCompletionEvent } from '../types/rtvi';
import { GoalCompletionPanel } from '../components/GoalCompletionPanel';
import { isHintResponse } from '../types/rtvi';

/**
 * Extract plain text from user message content.
 * Handles both new JSON format and legacy plain text format.
 *
 * Story 3.9.18: Unified Conversation JSON Format
 * New format: [{"persona": "User", "message": "Hello"}]
 * Legacy format: "Hello" (plain text)
 */
function extractUserMessageText(content: string): string {
  if (!content) return '';

  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const firstItem = parsed[0] as { persona?: string; message?: string };
      if (firstItem.message !== undefined) {
        return firstItem.message;
      }
    }
  } catch {
    // Not valid JSON - treat as plain text (legacy format)
  }

  return content;
}

/**
 * Connection state for WebRTC session
 */
const ConnectionState = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
} as const;

type ConnectionState = (typeof ConnectionState)[keyof typeof ConnectionState];

/**
 * Transcript message from conversation
 */
interface TranscriptMessage {
  timestamp: string;
  speaker: 'user' | 'bot' | string; // Allow persona names like "Narrator", "Vendor", etc.
  text: string;
  isFinal: boolean;
  actualTimestamp?: Date; // Actual Date object for merging logic
  audioFile?: string; // Audio filename for playback (e.g., "msg_0001_user.wav")
}

/**
 * RTVI server message from backend
 */
interface RTVIServerMessage {
  type: string;
  persona?: string;
  text?: string;
  goal_index?: number;
  timestamp?: string;
  [key: string]: unknown;
}

/**
 * Session data structure
 */
interface SessionData {
  sessionId: string;
  connect_url: string;
  livekit_url?: string;
  livekit_token?: string;
  livekit_room?: string;
  status: 'active' | 'completed' | 'finished';
}

/**
 * Pipecat transcript event data
 */
interface TranscriptEventData {
  text?: string;
  final?: boolean;
}

interface EpisodeTryViewInnerProps {
  episodeId: string | null;
  sessionId: string;
  sessionStatus: 'active' | 'completed' | 'finished' | null;
  messages: TranscriptMessage[];
  completedGoalIds: Set<string>;
  selectedLanguage: string | null;
  connectionState: ConnectionState;
  onConnectionStateChange: (state: ConnectionState) => void;
  onSessionStatusChange: (status: 'active' | 'completed' | 'finished') => void;
  onSessionDataUpdate: (data: SessionData) => void;
  onMessagesUpdate: (messages: TranscriptMessage[]) => void;
  hintPhrase: string | null;
  hintLoading: boolean;
  onIsPausedChange: (paused: boolean) => void;
  onBeforeDisconnect: () => void;
}

/**
 * Merge consecutive messages from the same speaker.
 * This handles:
 * - STT fragmentation where a single user utterance creates multiple messages
 * - TTS messages that are spoken sentence by sentence (narrator, personas)
 *
 * Merging rules:
 * - Merge ALL consecutive messages from the same speaker
 * - Stop merging when a different speaker appears
 * - Apply a safety timeout of 10 seconds for extremely long pauses (edge case)
 *
 * @param messages - Raw transcript messages
 * @param maxPauseMs - Maximum pause in milliseconds before treating as separate utterance (default: 10000ms)
 * @returns Merged messages array with continuous same-speaker messages combined
 */
function mergeConsecutiveUserMessages(
  messages: TranscriptMessage[],
  maxPauseMs: number = 10000 // 10 second safety timeout for very long pauses
): TranscriptMessage[] {
  if (messages.length === 0) return messages;

  const merged: TranscriptMessage[] = [];
  let currentMergedMessage: TranscriptMessage | null = null;

  for (const message of messages) {
    // If different speaker than current, flush accumulated message
    if (currentMergedMessage && currentMergedMessage.speaker !== message.speaker) {
      merged.push(currentMergedMessage);
      currentMergedMessage = null;
    }

    // Start new merged message or continue merging
    if (!currentMergedMessage) {
      // First message in sequence
      currentMergedMessage = { ...message };
    } else {
      // Check for extremely long pause (safety check)
      const currentTime = message.actualTimestamp || new Date();
      const previousTime = currentMergedMessage.actualTimestamp || new Date();
      const timeDiffMs = currentTime.getTime() - previousTime.getTime();

      if (timeDiffMs > maxPauseMs) {
        // Very long pause - treat as separate utterance
        merged.push(currentMergedMessage);
        currentMergedMessage = { ...message };
      } else {
        // Continue merging - same speaker is still talking
        currentMergedMessage.text = currentMergedMessage.text.trim() + ' ' + message.text.trim();
        currentMergedMessage.timestamp = message.timestamp; // Use latest timestamp
        currentMergedMessage.actualTimestamp = message.actualTimestamp;
      }
    }
  }

  // Flush remaining merged message
  if (currentMergedMessage) {
    merged.push(currentMergedMessage);
  }

  return merged;
}

/**
 * Inner component that uses the Pipecat client
 * Story 3.9.2: Language is read-only (selected via modal before navigation)
 */
function EpisodeTryViewInner({
  episodeId,
  sessionId,
  sessionStatus,
  messages,
  completedGoalIds,
  selectedLanguage,
  connectionState,
  onConnectionStateChange,
  onSessionStatusChange,
  onSessionDataUpdate,
  onMessagesUpdate: _onMessagesUpdate,
  hintPhrase,
  hintLoading,
  onIsPausedChange,
  onBeforeDisconnect,
}: EpisodeTryViewInnerProps) {
  const navigate = useNavigate();
  const client = usePipecatClient();

  // Refs
  const transcriptEndRef = useRef<HTMLDivElement>(null);
  // Track connection state in ref for use in closures (avoids stale state capture)
  const connectionStateRef = useRef<ConnectionState>(connectionState);
  connectionStateRef.current = connectionState; // Keep ref in sync with state

  // State management
  const [isConnecting, setIsConnecting] = useState(false);

  // Track the index of the most recently updated message for visual feedback
  const [lastUpdatedIndex, setLastUpdatedIndex] = useState<number>(-1);

  // Pause/Resume state - Story 3.9.8
  const [isPaused, setIsPaused] = useState(false);
  const [pauseStartTime, setPauseStartTime] = useState<Date | null>(null);
  const [pauseDuration, setPauseDuration] = useState<number>(0); // in seconds
  const pauseTimeoutRef = useRef<number | null>(null);
  const pauseDurationIntervalRef = useRef<number | null>(null);

  // Merge consecutive user messages for cleaner display
  const displayMessages = useMemo(() => {
    const merged = mergeConsecutiveUserMessages(messages);

    // Track which message was most recently updated (last message in merged array)
    if (merged.length > 0) {
      const lastIndex = merged.length - 1;
      const lastMessage = merged[lastIndex];

      // If last message is user message, it might still be receiving updates
      if (lastMessage.speaker === 'user') {
        setLastUpdatedIndex(lastIndex);

        // Clear the indicator after 2 seconds
        setTimeout(() => {
          setLastUpdatedIndex(-1);
        }, 2000);
      } else {
        setLastUpdatedIndex(-1);
      }
    }

    return merged;
  }, [messages]);

  // Fetch episode data
  const {
    data: episode,
    isLoading: isLoadingEpisode,
    error: episodeError,
  } = useEpisode(episodeId ?? undefined);

  // Get initial data (languages, durations)
  const { state: initialDataState } = useInitialData();
  const languages = initialDataState.data?.languages || [];
  const episodeDurations = initialDataState.data?.episodeDurations || [];

  // Helper to get duration display text (Story 15.11)
  const getDurationDisplay = (durationId: number) => {
    const duration = episodeDurations.find((d) => d.id === durationId);
    if (!duration) return `≤${durationId} turns`;
    return duration.localizations['en']?.name || `≤${duration.max_turns} turns`;
  };

  // Auto-scroll to latest message
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Reset isConnecting when connection state changes (except CONNECTING)
  useEffect(() => {
    if (connectionState !== ConnectionState.CONNECTING) {
      setIsConnecting(false);
    }
  }, [connectionState]);

  // Handle connect - calls connectSession API to get fresh WebRTC connection details
  const handleConnect = async () => {
    if (!selectedLanguage || !episodeId) return;

    setIsConnecting(true);

    try {
      // Step 1: Call connectSession API to get connect_url
      logger.info('Calling connectSession API', { sessionId });
      const connectResponse = await connectSession(sessionId);

      // Step 1b: POST to media endpoint to start bot and get LiveKit credentials
      const botResponse = await fetch(connectResponse.connect_url, { method: 'POST' });
      if (!botResponse.ok) {
        throw new Error(`Bot start failed: ${botResponse.status}`);
      }
      const botData = (await botResponse.json()) as {
        livekit_url: string;
        livekit_token: string;
        livekit_room: string;
      };

      // Update session data with LiveKit connection details from media endpoint
      onSessionDataUpdate({
        sessionId: connectResponse.session_id,
        connect_url: connectResponse.connect_url,
        livekit_url: botData.livekit_url,
        livekit_token: botData.livekit_token,
        livekit_room: botData.livekit_room,
        status: connectResponse.status,
      });

      // Note: Conversation history is now loaded from metadata on page load (lines 854-890)
      // No need to reload it here when connecting

      // Update session status (may have changed to completed/finished)
      onSessionStatusChange(connectResponse.status);

      logger.info('Session connected successfully', {
        sessionId: connectResponse.session_id,
        livekitRoom: botData.livekit_room,
      });

      // Step 2: Request microphone permission (before WebRTC connection)
      logger.info('Requesting microphone permission');
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error(
          'Your browser does not support microphone access. Please use a modern browser.'
        );
        setIsConnecting(false);
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true, // Prevents feedback loop (bot voice → mic)
          noiseSuppression: true, // Reduces background noise
          autoGainControl: true, // Normalizes volume levels
          sampleRate: 16000, // Matches Deepgram STT/TTS (reduces bandwidth 65%)
          channelCount: 1, // Mono audio (50% bandwidth reduction)
          sampleSize: 16, // 16-bit audio quality
        },
      });

      // Log applied audio settings for debugging
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        logger.info('Audio constraints applied', {
          echoCancellation: settings.echoCancellation,
          noiseSuppression: settings.noiseSuppression,
          autoGainControl: settings.autoGainControl,
          sampleRate: settings.sampleRate,
          channelCount: settings.channelCount,
          sampleSize: settings.sampleSize,
        });
      }

      stream.getTracks().forEach((track) => track.stop());
      logger.info('Microphone permission granted');

      // Step 3: WebRTC client will be auto-created and connected by parent component
      // The parent component will create a new PipecatClient via useMemo when sessionData changes
      // Then auto-connect via useEffect

      logger.info('Session preparation complete, WebRTC client will auto-connect');
      onConnectionStateChange(ConnectionState.CONNECTING);
      // Keep isConnecting=true until WebRTC connects (will be cleared by useEffect)
    } catch (error) {
      logger.error('Failed to connect:', error);
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          toast.error(
            'Microphone access was denied. Please enable microphone permissions in your browser settings.'
          );
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          toast.error('No microphone device found. Please connect a microphone and try again.');
        } else {
          toast.error(error.message || 'Failed to prepare session. Please try again.');
        }
      } else {
        toast.error('Failed to prepare session. Please try again.');
      }
      onConnectionStateChange(ConnectionState.ERROR);
      setIsConnecting(false);
    }
  };

  /**
   * Helper function: Send session.end message and wait for server disconnect
   * Story 3.9.6: Intentional Disconnect Handling
   */
  const disconnectSession = async () => {
    if (!client) return;

    try {
      if (connectionState === ConnectionState.CONNECTED) {
        await client.sendClientMessage('request.session.end', { reason: 'user_action' });
        logger.info('Sent request.session.end message to server');
      }
    } catch (error) {
      logger.warn('RTVI session end message failed:', error);
    }

    // Brief delay to let the RTVI message flush through the data channel,
    // then disconnect without waiting for server pipeline shutdown (can take seconds)
    await new Promise((r) => setTimeout(r, 200));
    await client.disconnect();
    onConnectionStateChange(ConnectionState.DISCONNECTED);
  };

  // Handle disconnect - "End Conversation" button (Save & Exit)
  const handleDisconnect = async () => {
    if (!client) return;

    try {
      onBeforeDisconnect();
      await disconnectSession();
      logger.info('Disconnect initiated (session remains active for reconnection)');
      toast.success('Session saved — you can reconnect later');
    } catch (error) {
      logger.error('Error during disconnect', error);
      toast.error('Error disconnecting');
      // Ensure we update connection state even on error
      onConnectionStateChange(ConnectionState.DISCONNECTED);
    }
  };

  // Handle finish session - "Finish Session" button
  // Story 3.9.6: Disconnect (if needed) then finish
  const handleFinishSession = async () => {
    // Ask for confirmation
    if (!confirm('Are you sure you want to finish this session? This action cannot be undone.')) {
      return;
    }

    try {
      // Step 1: Disconnect WebRTC if connected (using RTVI message)
      if (client && connectionState === ConnectionState.CONNECTED) {
        onBeforeDisconnect();
        await disconnectSession();
        logger.info('WebRTC session disconnected before finishing');

        // Wait for disconnect to complete (detected via state change)
        // Timeout after 5 seconds to prevent infinite wait
        await new Promise<void>((resolve) => {
          const startTime = Date.now();
          const maxWaitMs = 5000;
          const checkState = () => {
            // Use ref to get current state value (state variable is stale in closure)
            if (connectionStateRef.current === ConnectionState.DISCONNECTED) {
              resolve();
            } else if (Date.now() - startTime > maxWaitMs) {
              logger.warn('Disconnect wait timeout - proceeding with finish');
              resolve();
            } else {
              setTimeout(checkState, 100);
            }
          };
          // Start checking after brief delay to allow state propagation
          setTimeout(checkState, 100);
        });
      }

      // Step 2: Mark session as finished in database
      if (!sessionId) {
        throw new Error('Missing session ID');
      }
      await finishSession(sessionId);
      onSessionStatusChange('finished');
      logger.info('Session marked as finished', { sessionId });
      toast.success('Session finished successfully');
    } catch (error) {
      logger.error('Error finishing session', error);
      toast.error('Error finishing session');
    }
  };

  // Handle back navigation
  const handleBack = async () => {
    if (client && connectionState === ConnectionState.CONNECTED) {
      try {
        // Backend will close cleanly (transport state='disconnecting'), no error shown
        await disconnectSession();
        logger.info('Disconnected before navigation');
      } catch (error) {
        logger.error('Error during cleanup disconnect', error);
      }
    }
    navigate('/episodes');
  };

  /**
   * Handle pause - Story 3.9.8: Pause and Resume Functionality
   * - Mutes microphone using transport.enableMic()
   * - Sends pause.client.session RTVI message to server
   * - Starts 2-minute timeout timer for auto-disconnect
   */
  const handlePause = async () => {
    if (!client) return;

    try {
      // Step 1: Mute microphone using transport's enableMic function
      client.enableMic(false);
      logger.info('Microphone muted using transport.enableMic()');

      // Step 2: Send pause RTVI message to server
      await client.sendClientMessage('pause.client.session', {});
      logger.info('Sent pause.client.session message to server');

      // Step 3: Update UI state
      setIsPaused(true);
      const now = new Date();
      setPauseStartTime(now);
      setPauseDuration(0);

      // Step 4: Start pause duration counter (updates every second)
      pauseDurationIntervalRef.current = window.setInterval(() => {
        setPauseDuration((prev) => prev + 1);
      }, 1000);

      // Step 5: Start 2-minute timeout for auto-disconnect
      pauseTimeoutRef.current = window.setTimeout(
        async () => {
          logger.info('Pause timeout reached (2 minutes) - auto-disconnecting');

          // Backend will close cleanly (transport state='disconnecting'), no error shown
          // Disconnect WebRTC session (under the hood)
          await disconnectSession();

          // Note: UI still shows "Paused" state - no visual change
          // On resume, will automatically reconnect
        },
        2 * 60 * 1000
      ); // 2 minutes

      toast.success('Conversation paused');
    } catch (error) {
      logger.error('Error during pause:', error);
      toast.error('Failed to pause conversation');
    }
  };

  /**
   * Handle resume - Story 3.9.8: Pause and Resume Functionality
   * - Unmutes microphone using transport.enableMic()
   * - Sends resume.client.session RTVI message to server
   * - Clears pause timeout and duration timer
   * - If session disconnected (after 2-min timeout), automatically reconnects
   */
  const handleResume = async () => {
    if (!client) return;

    try {
      // Step 1: Clear pause timeout and duration timer
      if (pauseTimeoutRef.current !== null) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }

      if (pauseDurationIntervalRef.current !== null) {
        clearInterval(pauseDurationIntervalRef.current);
        pauseDurationIntervalRef.current = null;
      }

      // Step 2: Check if session disconnected (after 2-min timeout)
      if (connectionState === ConnectionState.DISCONNECTED) {
        logger.info('Session disconnected - auto-reconnecting before resume');

        // Reconnect session (seamless to user)
        await handleConnect();

        // Wait for connection to establish
        await new Promise<void>((resolve) => {
          const checkConnection = () => {
            // Use type assertion to avoid TypeScript narrowing issue in closure
            const currentState = connectionState as ConnectionState;
            if (currentState === ConnectionState.CONNECTED) {
              resolve();
            } else {
              setTimeout(checkConnection, 100);
            }
          };
          setTimeout(checkConnection, 100);
        });
      }

      // Step 3: Unmute microphone using transport's enableMic function
      client.enableMic(true);
      logger.info('Microphone unmuted using transport.enableMic()');

      // Step 4: Send resume RTVI message to server
      await client.sendClientMessage('resume.client.session', {});
      logger.info('Sent resume.client.session message to server');

      // Step 5: Update UI state
      setIsPaused(false);
      const pausedDurationMs = pauseStartTime ? new Date().getTime() - pauseStartTime.getTime() : 0;
      logger.info('Conversation resumed', { pauseDurationMs: pausedDurationMs });

      setPauseStartTime(null);
      setPauseDuration(0);

      toast.success('Conversation resumed');
    } catch (error) {
      logger.error('Error during resume:', error);
      toast.error('Failed to resume conversation');
    }
  };

  // Sync isPaused up to outer component so it can guard auto-hint requests
  useEffect(() => {
    onIsPausedChange(isPaused);
  }, [isPaused, onIsPausedChange]);

  // Cleanup pause timers on unmount or connection state change
  useEffect(() => {
    // Clear timers when disconnected or component unmounts
    if (connectionState === ConnectionState.DISCONNECTED) {
      if (pauseTimeoutRef.current !== null) {
        clearTimeout(pauseTimeoutRef.current);
        pauseTimeoutRef.current = null;
      }

      if (pauseDurationIntervalRef.current !== null) {
        clearInterval(pauseDurationIntervalRef.current);
        pauseDurationIntervalRef.current = null;
      }

      setIsPaused(false);
      setPauseStartTime(null);
      setPauseDuration(0);
    }

    // Cleanup on unmount
    return () => {
      if (pauseTimeoutRef.current !== null) {
        clearTimeout(pauseTimeoutRef.current);
      }
      if (pauseDurationIntervalRef.current !== null) {
        clearInterval(pauseDurationIntervalRef.current);
      }
    };
  }, [connectionState]);

  // Get connection state styling
  const getConnectionStateBadge = () => {
    const badges = {
      [ConnectionState.DISCONNECTED]: {
        text: 'Disconnected',
        className: 'bg-gray-100 text-gray-800',
      },
      [ConnectionState.CONNECTING]: {
        text: 'Connecting...',
        className: 'bg-yellow-100 text-yellow-800',
      },
      [ConnectionState.CONNECTED]: { text: 'Connected', className: 'bg-green-100 text-green-800' },
      [ConnectionState.ERROR]: { text: 'Error', className: 'bg-red-100 text-red-800' },
    };

    const badge = badges[connectionState];
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${badge.className}`}>
        {badge.text}
      </span>
    );
  };

  // Get language display name from initial data
  const getLanguageDisplayName = (languageCode: string): string => {
    const language = languages.find((lang) => lang.code === languageCode);
    return language?.name || languageCode.toUpperCase();
  };

  // Loading state
  if (isLoadingEpisode) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Error states
  if (episodeError) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="error-message">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading episode</p>
          <p className="text-gray-600 mb-4">{episodeError?.message}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Episodes
          </button>
        </div>
      </div>
    );
  }

  // No episode found
  if (!episode) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="not-found">
        <div className="text-center">
          <p className="text-gray-600 text-lg mb-4">Episode not found</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Episodes
          </button>
        </div>
      </div>
    );
  }

  // Render main view
  return (
    <>
      <div className="container mx-auto px-4 py-8" data-testid="episode-try-view">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">{episode.name}</h1>
            <button
              onClick={handleBack}
              className="text-blue-600 hover:text-blue-800 font-medium flex-shrink-0 ml-4"
              data-testid="back-button"
            >
              ← Back to Episodes
            </button>
          </div>
          <p className="text-gray-600 mb-4">{episode.context}</p>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div>
              <span className="mr-4">
                <strong>Duration:</strong> {getDurationDisplay(episode.duration.id)}
              </span>
              <span>
                <strong>Personas:</strong> {episode.personas.length}
              </span>
            </div>
            {getConnectionStateBadge()}
          </div>
        </div>

        {/* Main Content Grid - Three Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Controls */}
          <div className="lg:col-span-3 space-y-6">
            {/* Session Details Card (Story 3.9.2) */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Session Details</h2>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-600">Session ID:</span>
                  <p className="text-sm text-gray-900 font-mono break-all" data-testid="session-id">
                    {sessionId}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Language:</span>
                  <p className="text-sm text-gray-900" data-testid="selected-language">
                    {selectedLanguage ? getLanguageDisplayName(selectedLanguage) : 'Not selected'}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Status:</span>
                  <p className="text-sm text-gray-900" data-testid="session-status">
                    <span
                      className={`inline-block py-1 rounded text-xs font-medium ${
                        sessionStatus === 'active'
                          ? 'bg-green-100 text-green-800'
                          : sessionStatus === 'completed'
                            ? 'bg-blue-100 text-blue-800'
                            : sessionStatus === 'finished'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {sessionStatus
                        ? sessionStatus.charAt(0).toUpperCase() + sessionStatus.slice(1)
                        : 'Unknown'}
                    </span>
                  </p>
                </div>

                {/* Finish Session Button - Available for active sessions */}
                {sessionStatus === 'active' && (
                  <div className="pt-3 border-t border-gray-200">
                    <button
                      onClick={handleFinishSession}
                      className="w-full px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors font-medium"
                      data-testid="finish-session-button"
                    >
                      Finish Session
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      Mark this session as finished (cannot be resumed)
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Conversation Controls Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4">Conversation Controls</h2>

              {connectionState === ConnectionState.DISCONNECTED && (
                <button
                  onClick={handleConnect}
                  disabled={
                    !episodeId ||
                    !selectedLanguage ||
                    isConnecting ||
                    sessionStatus === 'completed' ||
                    sessionStatus === 'finished'
                  }
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                  data-testid="start-conversation-button"
                  title={
                    sessionStatus === 'completed' || sessionStatus === 'finished'
                      ? 'This session has ended and cannot be resumed'
                      : undefined
                  }
                >
                  {isConnecting
                    ? 'Connecting...'
                    : !episodeId
                      ? 'No Localizations'
                      : !selectedLanguage
                        ? 'Select Language First'
                        : sessionStatus === 'completed' || sessionStatus === 'finished'
                          ? 'Session Ended'
                          : 'Start Conversation'}
                </button>
              )}

              {connectionState === ConnectionState.CONNECTING && (
                <div className="text-center py-3" data-testid="connecting-status">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">Connecting to conversation...</p>
                </div>
              )}

              {connectionState === ConnectionState.CONNECTED && !isPaused && (
                <div className="space-y-3">
                  <button
                    onClick={handlePause}
                    className="w-full px-4 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-medium"
                    data-testid="pause-conversation-button"
                  >
                    Pause Conversation
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                    data-testid="end-conversation-button"
                  >
                    End Conversation
                  </button>
                </div>
              )}

              {connectionState === ConnectionState.CONNECTED && isPaused && (
                <div className="space-y-3">
                  {/* Paused Indicator with Duration */}
                  <div
                    className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
                    data-testid="paused-indicator"
                  >
                    <p className="text-yellow-800 text-sm font-medium mb-1">⏸️ Paused</p>
                    <p className="text-yellow-600 text-xs" data-testid="pause-duration">
                      Paused for {Math.floor(pauseDuration / 60)}:
                      {(pauseDuration % 60).toString().padStart(2, '0')}
                    </p>
                  </div>

                  {/* Resume Button */}
                  <button
                    onClick={handleResume}
                    className="w-full px-4 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium"
                    data-testid="resume-conversation-button"
                  >
                    Resume Conversation
                  </button>

                  {/* End Conversation Button (available while paused) */}
                  <button
                    onClick={handleDisconnect}
                    className="w-full px-4 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                    data-testid="end-conversation-while-paused-button"
                  >
                    End Conversation
                  </button>
                </div>
              )}

              {connectionState === ConnectionState.ERROR && (
                <div className="text-center py-3" data-testid="connection-error">
                  <p className="text-red-600 font-medium mb-3">Connection Error</p>
                  <button
                    onClick={handleConnect}
                    disabled={!selectedLanguage}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Retry Connection
                  </button>
                </div>
              )}
            </div>

            {/* Microphone Status Info */}
            {connectionState === ConnectionState.CONNECTED && !isPaused && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm font-medium mb-1">🎤 Microphone Active</p>
                <p className="text-blue-600 text-xs">
                  Make sure your microphone is enabled and speak clearly.
                </p>
              </div>
            )}

            {connectionState === ConnectionState.CONNECTED && isPaused && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-800 text-sm font-medium mb-1">🔇 Microphone Muted</p>
                <p className="text-gray-600 text-xs">
                  Your microphone is muted while paused. Press "Resume" to continue.
                </p>
              </div>
            )}
          </div>

          {/* Middle Column: Transcript */}
          <div className="lg:col-span-6">
            <div className="bg-white rounded-lg shadow-md p-6 h-[600px] flex flex-col">
              <h2 className="text-xl font-semibold mb-4">Live Transcript</h2>

              {/* Transcript Display Area */}
              <div
                className="flex-1 overflow-y-auto border border-gray-200 rounded-md p-4 mb-4"
                data-testid="transcript-display"
              >
                {displayMessages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-center">
                    <div>
                      <p className="text-gray-500 mb-2">No transcript yet</p>
                      <p className="text-gray-400 text-sm">
                        {connectionState === ConnectionState.CONNECTED
                          ? 'Start speaking to see the transcript here'
                          : 'Start a conversation to see the transcript'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {displayMessages.map((message, index) => {
                      const isUser = message.speaker === 'user';
                      const isNarrator = message.speaker.toLowerCase() === 'narrator';
                      const isUpdating = index === lastUpdatedIndex;

                      return (
                        <div
                          key={index}
                          className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                          data-testid={`transcript-message-${message.speaker}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-4 py-3 transition-all duration-300 ${
                              isUser
                                ? 'bg-blue-600 text-white'
                                : isNarrator
                                  ? 'bg-blue-50'
                                  : 'bg-gray-200 text-gray-900'
                            } ${isUpdating ? 'ring-2 ring-blue-400 ring-opacity-50 animate-pulse' : ''}`}
                          >
                            {isUser ? (
                              <>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-medium opacity-75">You</span>
                                  <span className="text-xs opacity-60">{message.timestamp}</span>
                                  {isUpdating && (
                                    <span className="text-xs opacity-75 italic ml-1">
                                      ✨ updating...
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm">{message.text}</p>
                              </>
                            ) : isNarrator ? (
                              <>
                                <div className="flex items-center gap-2 mb-2">
                                  <svg
                                    className="w-4 h-4 text-blue-700"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth="2"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                                    />
                                  </svg>
                                  <span className="text-xs font-semibold text-blue-700">
                                    {message.speaker}
                                  </span>
                                  <span className="text-xs opacity-60 text-blue-600">
                                    {message.timestamp}
                                  </span>
                                </div>
                                <p className="text-sm text-blue-900 italic border-l-4 border-blue-400 pl-3 whitespace-pre-wrap break-words">
                                  {message.text}
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-semibold text-green-700">
                                    {message.speaker}
                                  </span>
                                  <span className="text-xs opacity-60">{message.timestamp}</span>
                                </div>
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {message.text}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={transcriptEndRef} />
                  </div>
                )}
              </div>

              {/* Hint pill — auto-requested on turn_complete, no manual trigger */}
              {connectionState === ConnectionState.CONNECTED &&
                !isPaused &&
                (hintLoading || hintPhrase) && (
                  <div className="flex justify-center mb-3">
                    {hintLoading ? (
                      <div
                        className="flex items-center gap-3 px-6 py-3 bg-amber-50 border border-gray-200 rounded-full text-sm text-gray-400"
                        data-testid="hint-pill-loading"
                      >
                        <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        Getting hint...
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-3 px-6 py-3 bg-amber-50 border border-gray-200 rounded-full text-sm text-gray-600"
                        data-testid="hint-pill-phrase"
                      >
                        <svg
                          className="w-4 h-4 text-purple-500 flex-shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                          />
                        </svg>
                        <span>{hintPhrase}</span>
                      </div>
                    )}
                  </div>
                )}

              {/* Connection Info */}
              {connectionState === ConnectionState.CONNECTED && (
                <div className="text-xs text-gray-500 text-center">
                  Connected - LiveKit audio streaming active
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Goals */}
          <div className="lg:col-span-3">
            <GoalCompletionPanel episode={episode} completedGoalIds={completedGoalIds} />
          </div>
        </div>
      </div>

      {/* Audio is handled natively by LiveKit — no extra component needed */}
    </>
  );
}

/**
 * Outer component that creates and provides the Pipecat client
 * Story 3.9.2: Session-centric architecture
 * - Reads sessionId from path params (single source of truth)
 * - Fetches episodeId and language from session metadata on mount
 * - Connects to session ONLY when "Start Conversation" is clicked
 * - Calls connectSession on each reconnect for fresh WebRTC connection details
 */
export function EpisodeTryView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [episodeId, setEpisodeId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [completedGoalIds, setCompletedGoalIds] = useState<Set<string>>(new Set());
  const [goalsProgress, setGoalsProgress] = useState<GoalProgressEntry[]>([]); // Raw goals from backend
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.DISCONNECTED
  );
  const hasLoadedMetadata = useRef(false);
  const lastTransportStateRef = useRef<string>('disconnected'); // Track last transport state

  // Hint state - Story 3.9.22
  const [hintPhrase, setHintPhrase] = useState<string | null>(null);
  const [hintLoading, setHintLoading] = useState(false);
  const hintTimeoutRef = useRef<number | null>(null);
  // Refs for stable access inside useMemo callbacks (avoids stale closures)
  const pipecatClientRef = useRef<PipecatClient | null>(null);
  // Tracks why the next disconnect happens: 'user' | 'idle' | null (unexpected)
  const disconnectReasonRef = useRef<'user' | 'idle' | null>(null);
  const clientRef = useRef<typeof client>(null);
  const hintLoadingRef = useRef(false);
  const isPausedRef = useRef(false);

  // Fetch episode data (needed for goals matching)
  const { data: episode } = useEpisode(episodeId ?? undefined);

  // Derive completedGoalIds from goalsProgress when episode data is available
  // This matches goal_text from backend against episode.goals to find indices
  useEffect(() => {
    if (!episode?.goals || goalsProgress.length === 0) return;

    const goalIds = new Set<string>();
    for (const progress of goalsProgress) {
      // Find the index of this goal_text in episode.goals
      const index = episode.goals.findIndex((g) => g === progress.goal_text);
      if (index !== -1) {
        goalIds.add(String(index));
      } else {
        logger.warn('Goal text not found in episode goals', {
          goalText: progress.goal_text,
          episodeGoals: episode.goals,
        });
      }
    }

    // Only update if there are completed goals to avoid unnecessary renders
    if (goalIds.size > 0) {
      setCompletedGoalIds((prev) => {
        // Merge with any goals completed during live session
        const merged = new Set([...prev, ...goalIds]);
        return merged;
      });
      logger.info('Completed goals derived from goals_progress', {
        completedGoalCount: goalIds.size,
        goalIndices: Array.from(goalIds),
      });
    }
  }, [episode?.goals, goalsProgress]);

  // Fetch session metadata on mount to get episodeId and language
  useEffect(() => {
    if (!sessionId) {
      logger.error('Missing sessionId in path params');
      toast.error('Missing session ID. Please try again from Episodes list.');
      return;
    }

    // Prevent duplicate fetches (React Strict Mode calls effects twice in dev)
    if (hasLoadedMetadata.current) {
      logger.debug('Session metadata already loaded, skipping duplicate fetch');
      return;
    }

    const fetchSessionMetadata = async () => {
      try {
        hasLoadedMetadata.current = true;
        logger.info('Fetching session metadata', { sessionId });
        const metadata = await getSessionMetadata(sessionId);

        setEpisodeId(metadata.episode_id);
        setLanguage(metadata.language_code);
        logger.info('Session metadata loaded', {
          sessionId,
          episodeId: metadata.episode_id,
          language: metadata.language_code,
          status: metadata.status,
        });

        // Store goals_progress for later matching when episode data is available
        if (metadata.goals_progress && metadata.goals_progress.length > 0) {
          setGoalsProgress(metadata.goals_progress);
          logger.info('Goals progress loaded from metadata', {
            goalsProgressCount: metadata.goals_progress.length,
          });
        }

        // Load conversation history from metadata if available
        if (metadata.conversation_context && metadata.conversation_context.length > 0) {
          const historyMessages: TranscriptMessage[] = [];

          for (const turn of metadata.conversation_context) {
            const turnTimestamp = new Date(turn.timestamp).toLocaleTimeString();

            // Add user messages (flat format) with audio file reference
            // Story 3.9.18: Extract text from JSON format [{"persona": "User", "message": "..."}]
            if (turn.role === 'user') {
              historyMessages.push({
                timestamp: turnTimestamp,
                speaker: 'user',
                text: extractUserMessageText(turn.content || ''),
                isFinal: true,
                actualTimestamp: new Date(turn.timestamp),
                audioFile: turn.audio_file, // Preserve audio file reference
              });
            }

            // Add assistant messages with personas (flat format) with audio file references
            // Audio is stored at turn level (one file per assistant turn), assign to first persona only
            if (turn.role === 'assistant' && turn.personas) {
              for (let i = 0; i < turn.personas.length; i++) {
                const persona = turn.personas[i];
                historyMessages.push({
                  timestamp: turnTimestamp,
                  speaker: persona.persona,
                  text: persona.message,
                  isFinal: true,
                  actualTimestamp: new Date(turn.timestamp),
                  // Assign audio file to first persona only (audio covers all personas in turn)
                  audioFile: i === 0 ? turn.audio_file : undefined,
                });
              }
            }

            // Ignore tool messages (role="tool") - not displayed in transcript
          }

          setMessages(historyMessages);
          logger.info('Conversation history loaded from metadata', {
            messageCount: historyMessages.length,
          });
        }

        // Initialize session data with status (LiveKit fields filled on connect)
        setSessionData({
          sessionId: metadata.session_id,
          connect_url: '',
          livekit_url: '',
          livekit_token: '',
          livekit_room: '',
          status: metadata.status as 'active' | 'completed' | 'finished',
        });
      } catch (error) {
        logger.error('Failed to fetch session metadata', { sessionId, error });
        toast.error('Failed to load session. Please try again.');
      }
    };

    fetchSessionMetadata();
  }, [sessionId]);

  // Create Pipecat client with useMemo — cached via ref to prevent recreation
  // on sessionData status changes (e.g., after disconnect updates status)
  const client = useMemo(() => {
    if (
      !sessionData ||
      !sessionData.livekit_url ||
      !sessionData.livekit_token ||
      !sessionData.livekit_room
    )
      return null;

    // Reuse existing client if already created (prevents recreation on sessionData status changes)
    if (pipecatClientRef.current) {
      return pipecatClientRef.current;
    }

    const clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    logger.info('Creating PipecatClient', {
      clientId,
      livekitUrl: sessionData.livekit_url,
      livekitRoom: sessionData.livekit_room,
      sessionId: sessionData.sessionId,
    });

    const pipecatClient = new PipecatClient({
      transport: new WebLiveKitTransport({
        url: sessionData.livekit_url,
        token: sessionData.livekit_token,
        roomName: sessionData.livekit_room,
      }),
      enableMic: true,
      enableCam: false,
      callbacks: {
        onTransportStateChanged: (state) => {
          logger.info(`[${clientId}] Transport state changed:`, state);
          lastTransportStateRef.current = state;
        },
        onUserTranscript: (data: TranscriptEventData) => {
          logger.info(`[${clientId}] onUserTranscript CALLED`, { data });

          if (data.final === true) {
            const now = new Date();
            const message: TranscriptMessage = {
              timestamp: now.toLocaleTimeString(),
              speaker: 'user',
              text: data.text || '',
              isFinal: true,
              actualTimestamp: now,
            };
            setMessages((prev) => [...prev, message]);
          }
        },
        onBotTtsText: (data: TranscriptEventData | { text?: string; [key: string]: unknown }) => {
          logger.info(`[${clientId}] onBotTtsText CALLED`, { data });

          const text = data.text || String(data);

          // Parse persona from prefix: [Persona Name] message
          const personaMatch = text.match(/^\[([^\]]+)\]\s*(.*)$/);
          let speaker = 'Bot';
          let messageText = text;

          if (personaMatch) {
            speaker = personaMatch[1]; // "Narrator", "Street Food Vendor"
            messageText = personaMatch[2]; // Clean message without prefix
            logger.info(`[${clientId}] Parsed persona: ${speaker}`);
          }

          const now = new Date();
          const message: TranscriptMessage = {
            timestamp: now.toLocaleTimeString(),
            speaker,
            text: messageText,
            isFinal: true,
            actualTimestamp: now,
          };
          setMessages((prev) => [...prev, message]);
          logger.info(`[${clientId}] Added transcript: speaker="${speaker}"`);
        },
        onConnected: () => {
          logger.info('WebRTC room joined, waiting for bot...');
        },
        onBotReady: () => {
          logger.info('Bot ready — session active');
          setConnectionState(ConnectionState.CONNECTED);
        },
        onDisconnected: async () => {
          const lastTransportState = lastTransportStateRef.current;
          const reason = disconnectReasonRef.current;
          disconnectReasonRef.current = null;

          logger.info('WebRTC disconnected', { lastTransportState, reason });
          setConnectionState(ConnectionState.DISCONNECTED);

          // Known disconnect reason — show appropriate message, skip metadata fetch
          if (reason === 'user') {
            logger.info('User-initiated disconnect');
            return;
          }
          if (reason === 'idle') {
            toast('Session paused due to inactivity. You can reconnect.', { duration: 5000 });
            return;
          }

          // Unknown reason — fetch status from backend to determine what happened
          try {
            if (!sessionId) return;
            const metadata = await getSessionMetadata(sessionId);
            logger.info('Session status after disconnect', { newStatus: metadata.status });

            setSessionData((prev) => (prev ? { ...prev, status: metadata.status } : null));

            if (metadata.status === 'completed') {
              toast.success('Episode completed! All learning goals achieved.');
            } else if (metadata.status === 'finished') {
              toast('Session ended.');
            } else {
              logger.warn('Unexpected disconnect', { lastTransportState });
              toast.error('Connection lost. This may be due to an error.', { duration: 6000 });
            }
          } catch (error) {
            logger.error('Failed to fetch session status after disconnect', error);
          }
        },
        onError: (message) => {
          logger.error('WebRTC error', message);
        },
        onServerMessage: (message: RTVIServerMessage) => {
          logger.info(`[${clientId}] Server message received:`, { message });

          // Handle goal_completed events
          if (message.type === 'goal_completed' && message.goal_index !== undefined) {
            const goalIndex = String(message.goal_index);
            logger.info(`[${clientId}] Goal completed: index ${goalIndex}`);
            setCompletedGoalIds((prev) => new Set(prev).add(goalIndex));
          }

          // Auto-hint: request hint when bot finishes speaking - Story 3.9.22
          if (message.type === 'turn_complete') {
            if (!hintLoadingRef.current && !isPausedRef.current && clientRef.current) {
              hintLoadingRef.current = true;
              setHintLoading(true);
              setHintPhrase(null);
              try {
                clientRef.current.sendClientMessage('hint.request.client', {
                  timestamp: new Date().toISOString(),
                });
              } catch (err) {
                logger.error(`[${clientId}] Auto-hint request failed:`, err);
                setHintLoading(false);
              }
              if (hintTimeoutRef.current !== null) clearTimeout(hintTimeoutRef.current);
              hintTimeoutRef.current = window.setTimeout(() => {
                setHintLoading(false);
                hintTimeoutRef.current = null;
              }, 5000);
              logger.info(`[${clientId}] Auto-hint requested on turn_complete`);
            }
          }

          // Handle idle timeout — server is about to disconnect
          if (message.type === 'idle.server.timeout') {
            disconnectReasonRef.current = 'idle';
            logger.info(`[${clientId}] Server idle timeout — session will disconnect`);
          }

          // Handle hint response - Story 3.9.22
          if (isHintResponse(message)) {
            if (hintTimeoutRef.current !== null) {
              clearTimeout(hintTimeoutRef.current);
              hintTimeoutRef.current = null;
            }
            setHintPhrase(message.hint_text || null);
            setHintLoading(false);
            logger.info(`[${clientId}] Hint received: "${message.hint_text}"`);
          }
        },
      },
    });

    logger.info(`[${clientId}] PipecatClient created successfully`);
    pipecatClientRef.current = pipecatClient;
    return pipecatClient;
  }, [sessionData, sessionId]);

  // Connect to WebRTC when client is ready and user clicked "Start Conversation"
  useEffect(() => {
    if (client && connectionState === ConnectionState.CONNECTING) {
      logger.info('Connecting to WebRTC', { sessionId: sessionData?.sessionId });

      client
        .connect()
        .then(() => {
          logger.info('WebRTC connected successfully');
          toast.success('Connected! Start speaking to begin the conversation.');
        })
        .catch((error) => {
          logger.error('Failed to connect WebRTC', error);
          toast.error('Failed to connect to WebRTC. Please try again.');
          setConnectionState(ConnectionState.ERROR);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, connectionState]);

  // Cleanup client on unmount
  useEffect(() => {
    return () => {
      if (client) {
        logger.info('Cleaning up PipecatClient', { sessionId: sessionData?.sessionId });
        client.disconnect().catch(console.error);
        pipecatClientRef.current = null;
      }
    };
  }, [client, sessionData?.sessionId]);

  // Cleanup hint timeout on unmount
  useEffect(() => {
    return () => {
      if (hintTimeoutRef.current !== null) {
        clearTimeout(hintTimeoutRef.current);
      }
    };
  }, []);

  // Sync refs used inside useMemo callbacks
  useEffect(() => {
    clientRef.current = client;
  }, [client]);
  useEffect(() => {
    hintLoadingRef.current = hintLoading;
  }, [hintLoading]);

  // Handle session status change from inner component
  const handleSessionStatusChange = (status: 'active' | 'completed' | 'finished') => {
    setSessionData((prev) => (prev ? { ...prev, status } : null));
    logger.info('Session status updated', { sessionId, status });
  };

  // Handle session data update from inner component (when connectSession is called)
  const handleSessionDataUpdate = (data: SessionData) => {
    // Reset disconnect reason — new connection, clean slate
    disconnectReasonRef.current = null;
    // Clear cached client so useMemo creates a fresh one with new LiveKit credentials
    if (pipecatClientRef.current) {
      pipecatClientRef.current.disconnect().catch(() => {});
      pipecatClientRef.current = null;
    }
    setSessionData(data);
    logger.info('Session data updated', { sessionId: data.sessionId });
  };

  // Handle messages update from inner component (conversation history preload)
  const handleMessagesUpdate = (newMessages: TranscriptMessage[]) => {
    setMessages(newMessages);
    logger.info('Messages updated', { messageCount: newMessages.length });
  };

  const handleBeforeDisconnect = useCallback(() => {
    disconnectReasonRef.current = 'user';
  }, []);

  const handleIsPausedChange = useCallback((paused: boolean) => {
    isPausedRef.current = paused;
    if (!paused) {
      // Resumed — clear stale hint; turn_complete will auto-request a fresh one
      setHintPhrase(null);
      setHintLoading(false);
    }
  }, []);

  if (!client) {
    return (
      <EpisodeTryViewInner
        episodeId={episodeId}
        sessionId={sessionId!}
        sessionStatus={sessionData?.status || null}
        messages={messages}
        completedGoalIds={completedGoalIds}
        selectedLanguage={language}
        connectionState={connectionState}
        onConnectionStateChange={setConnectionState}
        onSessionStatusChange={handleSessionStatusChange}
        onSessionDataUpdate={handleSessionDataUpdate}
        onMessagesUpdate={handleMessagesUpdate}
        hintPhrase={hintPhrase}
        hintLoading={hintLoading}
        onIsPausedChange={handleIsPausedChange}
        onBeforeDisconnect={handleBeforeDisconnect}
      />
    );
  }

  return (
    <PipecatClientProvider client={client}>
      <EpisodeTryViewInner
        episodeId={episodeId}
        sessionId={sessionId!}
        sessionStatus={sessionData?.status || null}
        messages={messages}
        completedGoalIds={completedGoalIds}
        selectedLanguage={language}
        connectionState={connectionState}
        onConnectionStateChange={setConnectionState}
        onSessionStatusChange={handleSessionStatusChange}
        onSessionDataUpdate={handleSessionDataUpdate}
        onMessagesUpdate={handleMessagesUpdate}
        hintPhrase={hintPhrase}
        hintLoading={hintLoading}
        onIsPausedChange={handleIsPausedChange}
        onBeforeDisconnect={handleBeforeDisconnect}
      />
    </PipecatClientProvider>
  );
}
